import { ActionIcon, Box, Button, Group, Modal, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import React, { useState } from 'react';
import { DataTable } from 'mantine-datatable';
import useSortableData from 'src/common/components/table/hooks/useSortableData';
import { SortableColumn } from 'src/common/components/table/types';
import { textOrNotRecorded } from '../notRecorded';

type Column<T> = {
  key: keyof T;
  header: string;
  align?: 'left' | 'right';
  renderValue?: (value: T[keyof T], item: T) => React.ReactNode;
  sortable?: boolean;
  sortKey?: string;
  sortValueGetter?: (item: T) => unknown;
};

interface TableViewProps<T> {
  items: T[];
  columns: Column<T>[];
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
}

export function TableViewBase<T extends Record<string, unknown>>({
  items,
  columns,
  onEdit,
  onDelete,
}: TableViewProps<T>) {
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);
  const [itemToDelete, setItemToDelete] = useState<T | null>(null);

  const handleDeleteClick = (item: T) => {
    setItemToDelete(item);
    openDeleteModal();
  };

  const handleDeleteConfirm = () => {
    if (itemToDelete && onDelete) {
      onDelete(itemToDelete);
    }
    closeDeleteModal();
    setItemToDelete(null);
  };

  const dataTableColumns: SortableColumn<T>[] = [
    ...columns.map(col => ({
      accessor: String(col.key),
      title: col.header,
      sortable: col.sortable ?? false,
      sortKey: col.sortKey ?? String(col.key),
      sortValueGetter: col.sortValueGetter,
      textAlign: (col.align || 'left') as 'left' | 'right',
      render: (record: T) =>
        col.renderValue
          ? col.renderValue(record[col.key], record)
          : textOrNotRecorded(record[col.key]),
    })),
    ...(onEdit || onDelete
      ? [
          {
            accessor: 'actions',
            title: '',
            textAlign: 'right' as const,
            width: 100,
            render: (record: T) => (
              <Group gap="xs" justify="flex-end">
                {onEdit && (
                  <ActionIcon
                    variant="subtle"
                    color="gray.8"
                    onClick={() => onEdit(record)}
                    aria-label="Edit"
                  >
                    <IconPencil size={16} />
                  </ActionIcon>
                )}
                {onDelete && (
                  <ActionIcon
                    variant="subtle"
                    color="gray.8"
                    onClick={() => handleDeleteClick(record)}
                    aria-label="Delete"
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                )}
              </Group>
            ),
          },
        ]
      : []),
  ];

  const { sortStatus, setSortStatus, sortedData } = useSortableData(items, dataTableColumns);

  return (
    <>
      <Box>
        <DataTable
          columns={dataTableColumns}
          records={sortedData}
          sortStatus={sortStatus}
          onSortStatusChange={setSortStatus}
          variant="cashflows-table"
          withTableBorder={false}
          withRowBorders={false}
          minHeight={150}
        />
      </Box>

      <Modal opened={deleteModalOpened} onClose={closeDeleteModal} title="Are you sure?" centered>
        <Text c="gray.10">Are you sure you want to remove this item?</Text>
        <Group justify="flex-end" mt="xl">
          <Button variant="pill" onClick={closeDeleteModal}>
            Cancel
          </Button>
          <Button variant="pill" color="red" onClick={handleDeleteConfirm}>
            Remove
          </Button>
        </Group>
      </Modal>
    </>
  );
}
